import { useState } from "react";
import JsonEditor from "../../../CommonComponents/json_editor/JsonEditor";
import { InfoIcon, CaretIcon, CloseIcon, CopyIcon, CopiedCheckIcon } from "./svgs/TestCaseSvgs";
import { flattenPayload, getNestedValue, exportValidationRule, reconstructParamStates } from "./TestCaseHelpers";
import { TestCaseStep, ValidationRuleEntry } from "./TestCaseTypes";
import { fmtVal } from "./Utils";
import { showToastMessage } from "../../../../utils/commonFunctions";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";

interface StepDetailOverlayProps {
  step: TestCaseStep;
  onClose: () => void;
}

// Recursively removes keys where the leaf has isValidate: false
const filterValidatedOnly = (obj: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key] as Record<string, unknown>;
    if (val && typeof val === "object" && "isValidate" in val) {
      if (val.isValidate !== false) result[key] = val;
    } else if (val && typeof val === "object") {
      const nested = filterValidatedOnly(val);
      if (Object.keys(nested).length > 0) result[key] = nested;
    } else {
      result[key] = val;
    }
  }
  return result;
};

const getValidatedParamsFromValidation = (obj: any, prefix = ""): { key: string; displayName: string }[] => {
  if (obj == null || typeof obj !== "object") return [];
  if ("isValidate" in obj) {
    if (obj.isValidate) {
      const parts = prefix.split(".");
      const lastPart = parts[parts.length - 1] || prefix;
      return [{ key: prefix, displayName: lastPart }];
    }
    return [];
  }
  if (Array.isArray(obj)) {
    return obj.flatMap((item, idx) => {
      return getValidatedParamsFromValidation(item, `${prefix}[${idx}]`);
    });
  }
  return Object.entries(obj).flatMap(([k, v]) => {
    const isNumeric = /^\d+$/.test(k);
    const childPath = isNumeric
      ? `${prefix}[${k}]`
      : (prefix ? `${prefix}.${k}` : k);
    return getValidatedParamsFromValidation(v, childPath);
  });
};

const StepDetailOverlay = ({ step, onClose }: StepDetailOverlayProps): JSX.Element => {
  const [tmTab, setTmTab] = useState<"json" | "validations">("validations");
  const [expandedTMs, setExpandedTMs] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

  const detailJson = step?.tcJson ?? "";
  const tmList: ValidationRuleEntry[] = step?.validationRule ?? [];

  const handleCopy = (): void => {
    navigator.clipboard?.writeText(detailJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toggleTM = (label: string): void => {
    setExpandedTMs((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleCopyTmValidation = (tmLabel: string): void => {
    const tm = tmList.find((t) => t.label === tmLabel);
    if (!tm) {
      showToastMessage("error", "Telemetry not found");
      return;
    }
    const paramStates = reconstructParamStates([tm]);
    const tmState = paramStates[tmLabel];
    if (!tmState) {
      showToastMessage("error", "Failed to export validation config");
      return;
    }
    const config = exportValidationRule(tmLabel, tm.value, tmState);
    const jsonStr = JSON.stringify(config);
    navigator.clipboard?.writeText(jsonStr).then(() => {
      setCopiedLabel(tmLabel);
      setTimeout(() => setCopiedLabel(null), 2000);
      showToastMessage("success", `Validation config copied for ${tmLabel}`);
    }).catch(() => {
      showToastMessage("error", "Failed to copy to clipboard");
    });
  };

  return (
    <div className="tcd-detail__overlay">
      {/* Tab bar */}
      <div className="tcd-detail__tabs">
        <div className="tcd-detail__tab-left">
          <div className="tcd-detail__tab active">DETAILS</div>
        </div>
        <button className="tcd-detail__back" type="button" onClick={onClose} title="Collapse">
          <CloseIcon />
        </button>
      </div>

      {/* Two-panel body */}
      <div className="tcd-detail__body">
        {/* Left — TC JSON */}
        <div className="tcd-detail__panel">
          <div className="tcd-detail__section-header">
            <div className="tcd-detail__section-title-group">
              <span className="tcd-detail__section-title">TELECOMMAND</span>
            </div>
            <button
              className={`tcd-icon-btn${copied ? " tcd-icon-btn--copied" : ""}`}
              type="button"
              title={copied ? "Copied!" : "Copy JSON"}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CopiedCheckIcon />
                  <span className="tcd-icon-btn__label">Copied!</span>
                </>
              ) : (
                <CopyIcon />
              )}
            </button>
          </div>
          <div className="tcd-json-editor-wrap">
            <JsonEditor value={detailJson} editable={false} backgroundColor="rgba(9, 9, 9, 0.65)" />
          </div>
        </div>

        {/* Right — TM validations */}
        <div className="tcd-detail__panel">
          <div className="tcd-detail__section-header">
            <span className="tcd-detail__section-title">TELEMETRY</span>
            <div className="tcd-tm-tabs">
              <button type="button" className={`tcd-tm-tab${tmTab === "json" ? " active" : ""}`} onClick={() => setTmTab("json")}>TM JSON</button>
              <button type="button" className={`tcd-tm-tab${tmTab === "validations" ? " active" : ""}`} onClick={() => setTmTab("validations")}>VALIDATIONS</button>
            </div>
          </div>
          <div className="tcd-tm-content">
            {tmTab === "json" ? (
              <div className="tcd-tm-json-editor-wrap">
                <JsonEditor
                  value={tmList.length === 0 ? "// No telemetry" : JSON.stringify(
                    tmList.map((t) => {
                      const { payloadSchema: _, ...rest } = t.value as unknown as Record<string, unknown> & { payloadSchema?: unknown };
                      return { ...rest, payload: filterValidatedOnly(t.validation as Record<string, unknown>) };
                    }),
                    null, 2
                  )}
                  editable={false}
                  backgroundColor="rgba(9, 9, 9, 0.65)"
                />
              </div>
            ) : tmList.length === 0 ? (
              <div className="tcd-tm-empty"><InfoIcon /><span>No telemetry configured</span></div>
            ) : (
              tmList.map((tm) => {
                const isExpanded = expandedTMs.includes(tm.label);
                const checkedParams = getValidatedParamsFromValidation(tm.validation);
                return (
                  <div key={tm.label} className="tcd-detail__tm-item">
                    <div className="tcd-detail__tm-header" onClick={() => toggleTM(tm.label)}>
                      <span className="tcd-detail__tm-caret">
                        <CaretIcon expanded={isExpanded} />
                      </span>
                      <span className="tcd-detail__tm-label">{tm.label}</span>
                      <CustomToolTip title="Copy validation rules to clipboard" placement="top">
                        <button
                          type="button"
                          className={`tcd-detail__tm-copy-btn${copiedLabel === tm.label ? " copied" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyTmValidation(tm.label);
                          }}
                        >
                          {copiedLabel === tm.label ? <CopiedCheckIcon /> : <CopyIcon />}
                        </button>
                      </CustomToolTip>
                    </div>
                    {isExpanded && (
                      <div className="tcd-detail__tm-body">
                        {checkedParams.length === 0 ? (
                          <div className="tcd-detail__tm-empty-params">No parameters configured</div>
                        ) : (
                          checkedParams.map(({ key, displayName }) => (
                            <div key={key} className="tcd-detail__param-row">
                              <span className="tcd-detail__param-name">{displayName}</span>
                              <span className="tcd-detail__param-value">{fmtVal(getNestedValue(tm.validation, key))}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetailOverlay;
